import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const variants = {
  primary: `
    bg-gradient-to-r from-primary-500 via-primary-600 to-teal-500
    text-white font-semibold
    shadow-lg shadow-primary-500/25
    hover:shadow-xl hover:shadow-primary-500/30
    border-0
  `,
  secondary: `
    bg-white/10 backdrop-blur-sm
    text-white font-semibold
    border-2 border-white/30
    hover:bg-white/20 hover:border-white/50
  `,
  outline: `
    bg-transparent
    text-primary-600 font-semibold
    border-2 border-primary-500
    hover:bg-primary-50
  `,
  ghost: `
    bg-transparent
    text-dark-700 font-medium
    hover:bg-dark-100
  `,
  glass: `
    bg-white/20 backdrop-blur-xl
    text-white font-semibold
    border border-white/30
    shadow-glass
    hover:bg-white/30
  `,
};

const sizes = {
  sm: "px-4 py-2 text-sm rounded-lg",
  md: "px-6 py-3 text-base rounded-xl",
  lg: "px-8 py-4 text-lg rounded-xl",
};

export default function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  href,
  to,
  icon,
  iconRight,
  disabled = false,
  loading = false,
  onClick,
  type = "button",
}) {
  const baseClasses = `
    inline-flex items-center justify-center gap-2
    transition-all duration-300
    disabled:opacity-50 disabled:cursor-not-allowed
    ${variants[variant]}
    ${sizes[size]}
    ${className}
  `;

  const content = (
    <>
      {loading ? (
        <svg
          className="animate-spin h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : icon ? (
        <span className="w-5 h-5">{icon}</span>
      ) : null}
      {children}
      {iconRight && <span className="w-5 h-5">{iconRight}</span>}
    </>
  );

  if (to) {
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Link to={to} className={baseClasses}>
          {content}
        </Link>
      </motion.div>
    );
  }

  if (href) {
    return (
      <motion.a
        href={href}
        className={baseClasses}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button
      type={type}
      className={baseClasses}
      onClick={onClick}
      disabled={disabled || loading}
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
    >
      {content}
    </motion.button>
  );
}
